import type { GetServerSideProps, NextPage } from 'next';

import { useRouter } from 'next/router';

import CatalogSidebar from '../../components/catalog/CatalogSidebar';
import ProductsList from '../../components/catalog/ProductsList';
import Breadcrumbs from '../../components/layout/Breadcrumbs';
import MetaTags from '../../components/layout/MetaTags';
import Pagination from '../../components/layout/Pagination';
import capacitiesService from '../../services/capacitiesService';
import productsService from '../../services/productsService';
import styles from '../../styles/CatalogPage.module.scss';
import type CapacityType from '../../types/CapacityType';
import type CategoryType from '../../types/CategoryType';
import type ProductType from '../../types/ProductType';

export const getServerSideProps: GetServerSideProps = async ({ query }) => {
  const slug = String(query.slug);
  const { category } = await productsService.getCategory(slug);
  const { categories } = await productsService.getCategories();
  const { capacities } = await capacitiesService.getCapacities();
  const selectedCapacity = query.size
    ? capacities.find((item) => item.id === Number(query.size))
    : null;

  const { products, page, total_pages, total_count } =
    await productsService.getProducts(
      {
        categoryId: category.id,
        capacity: selectedCapacity?.id,
        query: query.query ? String(query.query) : undefined,
      },
      String(query.page || '')
    );

  return {
    props: {
      category,
      categories,
      capacities,
      selectedCapacity,
      products,
      page,
      totalPages: total_pages,
      totalCount: total_count,
    },
  };
};

interface Props {
  category: CategoryType;
  categories: Array<CategoryType>;
  capacities: Array<CapacityType>;
  selectedCapacity: CapacityType | null;
  products: Array<ProductType>;
  page: number;
  totalPages: number;
  totalCount: number;
}

const CatalogPage: NextPage<Props> = ({
  category,
  categories,
  capacities,
  selectedCapacity,
  products,
  page,
  totalPages,
  totalCount,
}) => {
  const router = useRouter();
  return (
    <div className={styles.catalogPage}>
      <MetaTags title={category.name} />
      <div className="container">
        <div className={styles.catalogCols}>
          <CatalogSidebar
            categories={categories}
            selectedCategory={category}
            capacities={capacities}
            selectedCapacity={selectedCapacity}
          />
          <div>
            <Breadcrumbs
              items={
                selectedCapacity
                  ? [
                      ['Strona główna', '/'],
                      ['Produkty', '/katalog'],
                      [category.name, `/katalog/${category.slug}`],
                      selectedCapacity.name,
                    ]
                  : [
                      ['Strona główna', '/'],
                      ['Produkty', '/katalog'],
                      category.name,
                    ]
              }
            />
            <h1 className="pageHeading">{category.name}</h1>
            {category.description && (
              <p className={styles.catalogDescription}>
                {category.description}
              </p>
            )}

            <div className={styles.catalogResults}>
              znaleziono: <strong>{totalCount} wyników</strong>
            </div>

            <ProductsList products={products} />

            <Pagination
              page={page}
              totalPages={totalPages}
              onPageChange={(page) =>
                router.push({
                  pathname: `/katalog/${category.slug}`,
                  query: { page, capacity: selectedCapacity?.id },
                })
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CatalogPage;
